import React, { useState, useEffect, useContext, useRef } from "react";
import { StyleSheet, ScrollView, Text, TouchableOpacity, Modal as RNModal, Pressable } from "react-native";
import { View } from "../../../components/shared/Themed";
import { LabelBannerWithSearchBar } from "../../../components/custom/Banners/LabelBannerWithSearchBar";
import Spinner from "../../../components/shared/Spinner";
import { getRawMaterialProductionStocks } from "../../../services/stockService";

import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { default as rawMaterial } from "../utils/rawMaterialStockHeaders";
import { rawMaterialStockModel } from "../../../models/reportsModel";
import { DataTableWithPagination } from "../../../components/custom/DataTable/DataTableWithPagination";
import { useSelector } from "react-redux";
import { sortColumns } from "../../../utils/sortColumns";
import { handleTableLoader } from "../../../utils/common";
import { filterBasedOnAnyKey } from "../../../utils/searchInArrays";
import AsyncStorage from "@react-native-async-storage/async-storage";
// @ts-ignore
import { Feather } from "@expo/vector-icons";

export default function RawMaterialStocksReportScreen({
  route,
  navigation,
}: any) {
  const { isLargeDevice, isMediumDevice, isMobileDevice } = useContext(
    DeviceOrientationContext
  );

  const [isRefresh, setRefresh] = useState<boolean>(false);
  const { rawMaterialStockReport, isRawMaterialStockReportListLoaded } =
    useSelector((state: any) => state.reports);

  const [selected, setSelected] = useState<rawMaterialStockModel>(
    {} as unknown as rawMaterialStockModel
  );

  const [rawMaterialStockPageLevelList, setRawMaterialStockPageLevelList] =
    useState<Array<rawMaterialStockModel>>([]);
  const [isSearched, setIsSearched] = useState<boolean>(false);

  const [tableHeaders, setTableHeaders] = useState<Record<string, Record<string, string | number | boolean>>>(
    rawMaterial.headersRawMaterial
  );
  const [searchedText, setSearchedText] = useState<string>("");

  // Column visibility settings
  const [columnSettingsOpen, setColumnSettingsOpen] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [settingsBtnPos, setSettingsBtnPos] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const settingsBtnRef = useRef<any>(null);

  // Load saved hidden columns from storage on mount
  useEffect(() => {
    AsyncStorage.getItem("raw_material_stocks_report_hidden_columns").then((stored) => {
      if (stored) {
        try {
          setHiddenColumns(JSON.parse(stored));
        } catch { }
      }
    });
  }, []);

  // All available headers based on device size
  const getAllHeaders = () => {
    return isLargeDevice
      ? rawMaterial.headersRawMaterial
      : isMediumDevice
        ? rawMaterial.mediumDeviceHeadersRawMaterial
        : rawMaterial.smallDeviceHeadersRawMaterial;
  };

  const getFilteredHeaders = (baseHeaders: Record<string, Record<string, string | number | boolean>>) => {
    const visibleEntries = Object.entries(baseHeaders).filter(([key]) => !hiddenColumns.includes(key));
    const totalOriginalFlex = Object.values(baseHeaders).reduce((sum, h) => sum + (Number(h.width) || 0), 0);
    const totalVisibleFlex = visibleEntries.reduce((sum, [, h]) => sum + (Number(h.width) || 0), 0);
    const scale = totalVisibleFlex > 0 ? totalOriginalFlex / totalVisibleFlex : 1;

    const result: Record<string, Record<string, string | number | boolean>> = {};
    visibleEntries.forEach(([key, value]) => {
      result[key] = { ...value, width: Number(value.width) * scale };
    });
    return result;
  };

  // Update table headers when hidden columns change or device size changes
  useEffect(() => {
    const allHeaders = getAllHeaders();
    setTableHeaders(getFilteredHeaders(allHeaders));
  }, [hiddenColumns, isLargeDevice, isMediumDevice, isMobileDevice]);

  const toggleColumn = (columnKey: string) => {
    setHiddenColumns((prev) => {
      const updated = prev.includes(columnKey)
        ? prev.filter((k) => k !== columnKey)
        : [...prev, columnKey];
      AsyncStorage.setItem("raw_material_stocks_report_hidden_columns", JSON.stringify(updated));
      return updated;
    });
  };

  const displayTableLoader = () => {
    handleTableLoader(true, false, setIsSearched);
  };

  const fadeTableLoader = () => {
    handleTableLoader(false, true, setIsSearched);
  };

  const onHandleSortColumn = (columnKey: string) => {
    const _sortedRawMaterialStockList: Array<rawMaterialStockModel> =
      sortColumns(
        rawMaterialStockPageLevelList,
        columnKey,
        tableHeaders,
        setTableHeaders
      );

    setRawMaterialStockPageLevelList(_sortedRawMaterialStockList);
  };

  const handleSearch = (searchText: string) => {
    displayTableLoader();

    if (searchText.trim() !== "") {
      setSearchedText(searchText);

      const filteredListBasedOnSearchOrCondition: Array<rawMaterialStockModel> =
        filterBasedOnAnyKey(rawMaterialStockReport, searchText);

      setRawMaterialStockPageLevelList(filteredListBasedOnSearchOrCondition);
    } else {
      setSearchedText("");
      setRawMaterialStockPageLevelList(rawMaterialStockReport);
    }

    fadeTableLoader();
  };

  useEffect(() => {
    let isMounted = true;

    // Avoid keep refreshing table when item is selected
    if (isMounted) {
      if (rawMaterialStockPageLevelList.length === 0 || isRefresh) {
        displayTableLoader();
        getRawMaterialProductionStocks(tableHeaders);
      }
    }

    const allHeaders = getAllHeaders();
    setTableHeaders(getFilteredHeaders(allHeaders));

    setRefresh(false);

    return () => {
      isMounted = false;
    };
  }, [selected, isRefresh]);

  useEffect(() => {
    setRawMaterialStockPageLevelList(rawMaterialStockReport);

    setTimeout(() => {
      fadeTableLoader();
    }, 2000);
  }, [rawMaterialStockReport]);

  return (
    <ScrollView style={{ backgroundColor: "white" }}>
      <View style={[styles.container, { backgroundColor: "#f2f2f2" }]}>
        <View
          style={[
            styles.containerContent,
            {
              width: "100%",
            },
          ]}
        >
          {isRawMaterialStockReportListLoaded ? (
            <View>
              <LabelBannerWithSearchBar
                titleName={`Total Items: ${rawMaterialStockPageLevelList.length}`}
                handleSearch={handleSearch}
                setRefresh={setRefresh}
                dataToExport={rawMaterialStockPageLevelList}
                fileName="raw_material_stocks"
                extraControls={(
                  <TouchableOpacity
                    ref={settingsBtnRef}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      backgroundColor: "#F3F4F6",
                      justifyContent: "center",
                      alignItems: "center",
                      borderWidth: 1,
                      borderColor: columnSettingsOpen ? "#818CF8" : "#E2E8F0",
                    }}
                    onPress={() => {
                      if (settingsBtnRef.current) {
                        settingsBtnRef.current.measureInWindow(
                          (x: number, y: number, width: number, height: number) => {
                            setSettingsBtnPos({ x: x || 0, y: y || 0, width: width || 0, height: height || 0 });
                            setColumnSettingsOpen(!columnSettingsOpen);
                          }
                        );
                      } else {
                        setColumnSettingsOpen(!columnSettingsOpen);
                      }
                    }}
                  >
                    <Feather
                      name="settings"
                      size={15}
                      color={columnSettingsOpen ? "#56B7FD" : "#6B7280"}
                    />
                  </TouchableOpacity>
                )}
              />
              <DataTableWithPagination
                headers={tableHeaders}
                rows={rawMaterialStockPageLevelList}
                itemsPerPage={100}
                useModal={false}
                isDetail={false}
                isDataLoading={isSearched}
                setModalVisible={() => { }}
                setSelected={setSelected}
                handleRowClick={() => { }}
                handleDeleteRow={() => { }}
                handleSortColumn={onHandleSortColumn}
              />
              {columnSettingsOpen && (
                <RNModal
                  transparent={true}
                  visible={columnSettingsOpen}
                  animationType="fade"
                  onRequestClose={() => setColumnSettingsOpen(false)}
                >
                  <Pressable
                    style={{
                      flex: 1,
                      backgroundColor: "rgba(0,0,0,0.1)",
                    }}
                    onPress={() => setColumnSettingsOpen(false)}
                  >
                    <Pressable
                      style={{
                        position: "absolute",
                        top: settingsBtnPos.y + settingsBtnPos.height + 6,
                        left: settingsBtnPos.x - 180 + 40,
                        backgroundColor: "#fff",
                        borderRadius: 12,
                        borderWidth: 1,
                        borderColor: "#E2E8F0",
                        paddingVertical: 10,
                        minWidth: 220,
                        shadowColor: "#000",
                        shadowOffset: { width: 0, height: 8 },
                        shadowOpacity: 0.15,
                        shadowRadius: 20,
                        elevation: 10,
                      }}
                      onPress={(e) => e.stopPropagation()}
                    >
                      <Text
                        style={{
                          fontSize: 12,
                          fontWeight: "700",
                          color: "#6B7280",
                          paddingHorizontal: 16,
                          paddingBottom: 8,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Show / Hide Columns
                      </Text>
                      <View style={{ height: 1, backgroundColor: "#F1F5F9", marginBottom: 4 }} />
                      {/* Select All */}
                      <TouchableOpacity
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          paddingVertical: 8,
                          paddingHorizontal: 16,
                        }}
                        onPress={() => {
                          const allKeys = Object.keys(getAllHeaders()).filter((k) => k !== "");
                          if (hiddenColumns.length === 0) {
                            const updated = [...allKeys];
                            setHiddenColumns(updated);
                            AsyncStorage.setItem("raw_material_stocks_report_hidden_columns", JSON.stringify(updated));
                          } else {
                            setHiddenColumns([]);
                            AsyncStorage.setItem("raw_material_stocks_report_hidden_columns", JSON.stringify([]));
                          }
                        }}
                      >
                        <View
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: 4,
                            borderWidth: 1.5,
                            borderColor: hiddenColumns.length === 0 ? "#56B7FD" : "#D1D5DB",
                            backgroundColor: hiddenColumns.length === 0 ? "#56B7FD" : "#fff",
                            justifyContent: "center",
                            alignItems: "center",
                            marginRight: 10,
                          }}
                        >
                          {hiddenColumns.length === 0 && (
                            <Feather name="check" size={13} color="#fff" />
                          )}
                        </View>
                        <Text
                          style={{
                            fontSize: 14,
                            color: "#111827",
                            fontWeight: "700",
                          }}
                        >
                          Select All
                        </Text>
                      </TouchableOpacity>
                      <View style={{ height: 1, backgroundColor: "#F1F5F9", marginBottom: 2 }} />
                      <ScrollView style={{ maxHeight: 300 }} showsVerticalScrollIndicator={true}>
                        {Object.entries(getAllHeaders()).map(([key, value]) => {
                          if (key === "") return null;
                          const isVisible = !hiddenColumns.includes(key);
                          return (
                            <TouchableOpacity
                              key={key}
                              style={{
                                flexDirection: "row",
                                alignItems: "center",
                                paddingVertical: 8,
                                paddingHorizontal: 16,
                              }}
                              onPress={() => toggleColumn(key)}
                            >
                              <View
                                style={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: 4,
                                  borderWidth: 1.5,
                                  borderColor: isVisible ? "#56B7FD" : "#D1D5DB",
                                  backgroundColor: isVisible ? "#56B7FD" : "#fff",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  marginRight: 10,
                                }}
                              >
                                {isVisible && (
                                  <Feather name="check" size={13} color="#fff" />
                                )}
                              </View>
                              <Text
                                style={{
                                  fontSize: 14,
                                  color: isVisible ? "#111827" : "#9CA3AF",
                                  fontWeight: isVisible ? "600" : "400",
                                }}
                              >
                                {(value as any).name}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </ScrollView>
                    </Pressable>
                  </Pressable>
                </RNModal>
              )}
            </View>
          ) : (
            <View>
              <Spinner
                size="small"
                color="#74BBFF"
                styles={{ flexDirection: "row", margin: "20%" }}
              />
            </View>
          )}
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
  },
});
